import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send, Plus } from "lucide-react";

const CHANNELS = ["Sunday Bulletin", "Worship Slide", "Social Media", "Email Blast", "Website Banner", "All of the Above"];

export default function Announcements() {
  const { user } = useAuth() || {};
  const [form, setForm] = useState({
    ministry: user?.ministry || "",
    announcement_title: "",
    announcement_text: "",
    channels: "Sunday Bulletin",
    needed_by: "",
    event_date: "",
    contact_email: user?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api.get("/announcements/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/announcements", form);
      toast.success("Announcement request submitted!");
      setShowForm(false);
      setForm({ ...form, announcement_title: "", announcement_text: "", needed_by: "", event_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="announcements-page">
      <PageHero
        overline="Leadership Portal · Announcements"
        title={<>Promote your <span className="brand-gradient-text">ministry message</span>.</>}
        kicker="Request announcements to be shared in the bulletin, social media, worship slides, or email blasts."
      />

      <Container className="pb-32">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Overline>Your Requests</Overline>
            <h2 className="font-sans text-3xl md:text-4xl font-black">My Announcement Requests</h2>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-wine px-6 py-3 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2"
            data-testid="new-announcement-btn"
          >
            <Plus className="h-4 w-4" /> {showForm ? "Close" : "New Request"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="glass p-10 mb-12 grid md:grid-cols-2 gap-x-10 gap-y-6" data-testid="announcement-form">
            <Field label="Ministry / Group" required>
              <input data-testid="ann-ministry" required value={form.ministry} onChange={u("ministry")} className={inputCls} />
            </Field>
            <Field label="Distribution Channel" required>
              <select data-testid="ann-channel" required value={form.channels} onChange={u("channels")} className={inputCls}>
                {CHANNELS.map((c) => <option key={c} value={c} className="bg-royal">{c}</option>)}
              </select>
            </Field>
            <Field label="Announcement Title" required className="md:col-span-2">
              <input data-testid="ann-title" required value={form.announcement_title} onChange={u("announcement_title")} className={inputCls} />
            </Field>
            <Field label="Announcement Text" required className="md:col-span-2">
              <textarea data-testid="ann-text" required rows={5} value={form.announcement_text} onChange={u("announcement_text")} className={inputCls} placeholder="Date, time, location, audience, key details. Keep it concise and clear." />
            </Field>
            <Field label="Needed By" required>
              <input data-testid="ann-needed" type="date" required value={form.needed_by} onChange={u("needed_by")} className={inputCls} />
            </Field>
            <Field label="Event Date">
              <input data-testid="ann-event" type="date" value={form.event_date} onChange={u("event_date")} className={inputCls} />
            </Field>
            <Field label="Contact Email" required className="md:col-span-2">
              <input data-testid="ann-email" type="email" required value={form.contact_email} onChange={u("contact_email")} className={inputCls} />
            </Field>
            <div className="md:col-span-2 flex justify-end pt-4 border-t border-white/10">
              <button type="submit" disabled={submitting} className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="ann-submit">
                <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        )}

        {mine.length === 0 ? (
          <div className="glass p-12 text-center">
            <p className="text-white/65 text-lg italic">You haven't submitted any announcement requests yet.</p>
          </div>
        ) : (
          <div className="space-y-px bg-white/5">
            {mine.map((r) => (
              <div key={r.id} className="bg-royal/40 p-8 grid md:grid-cols-12 gap-6 items-center" data-testid={`my-announcement-${r.id}`}>
                <div className="md:col-span-2 text-xs uppercase tracking-widest text-ember font-bold">{r.channels}</div>
                <div className="md:col-span-6">
                  <h4 className="font-sans text-xl font-extrabold">{r.announcement_title}</h4>
                  <p className="text-sm text-white/65 line-clamp-2">{r.announcement_text}</p>
                </div>
                <div className="md:col-span-2 text-sm text-white/60">Need by:<br/><span className="text-white">{r.needed_by}</span></div>
                <div className="md:col-span-2 text-right">
                  <span className={`inline-block px-4 py-1 text-xs uppercase tracking-widest rounded-full font-bold ${
                    r.status === "pending" ? "bg-gold/20 text-gold" :
                    r.status === "completed" ? "bg-green-500/20 text-green-300" :
                    "bg-white/10 text-white/70"
                  }`}>{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-0 border-b border-white/20 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";
function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-bold">{label}{required && <span className="text-gold ml-1">*</span>}</div>
      {children}
    </label>
  );
}
